import React from "react";
import { Lock, Unlock, Sparkles, Plus, X, Maximize2, Minimize2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { LockedSectionView } from "./LockedSectionView";

export function EditorSectionsPanel({
  formData,
  setFormData,
  editorExpanded,
  setEditorExpanded,
  onUpdateSection,
  setActiveSection,
}) {
  const hasSections = formData.sections.length > 0;

  return (
    <>
      {hasSections ? (
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">
              Contract Sections
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditorExpanded(!editorExpanded)}
              className="h-7 w-7 p-0 text-slate-400 hover:text-slate-700"
              data-testid="expand-editor-btn"
              title={editorExpanded ? "Collapse editor" : "Expand editor"}
            >
              {editorExpanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <Accordion type="multiple" className="space-y-2">
              {formData.sections.map((section) => (
                <AccordionItem
                  key={section.id}
                  value={section.id}
                  className="border rounded-sm px-4"
                >
                  <AccordionTrigger className="hover:no-underline py-3">
                    <div className="flex items-center gap-3">
                      {section.is_editable ? (
                        <Unlock size={14} className="text-emerald-500" />
                      ) : (
                        <Lock size={14} className="text-amber-500" />
                      )}
                      <span className="font-medium">{section.name}</span>
                      <Badge variant={section.is_editable ? "outline" : "secondary"} className="text-xs">
                        {section.is_editable ? "Editable" : "Locked"}
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-4">
                    <SectionContent
                      section={section}
                      formData={formData}
                      setFormData={setFormData}
                      onUpdateSection={onUpdateSection}
                      setActiveSection={setActiveSection}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {/* Additional Clauses */}
            {formData.additional_clauses.length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-sm font-mono uppercase tracking-wider text-slate-500 mb-3">
                  Additional Clauses
                </h3>
                <div className="space-y-3">
                  {formData.additional_clauses.map((clause) => (
                    <div key={clause.id} className="border rounded-sm p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium">{clause.name}</p>
                          <Badge variant="outline" className="text-xs">
                            {clause.category}
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500"
                          onClick={() => setFormData((prev) => ({
                            ...prev,
                            additional_clauses: prev.additional_clauses.filter((c) => c.id !== clause.id),
                          }))}
                        >
                          <X size={14} />
                        </Button>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
                        {clause.content}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">
              Contract Content
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditorExpanded(!editorExpanded)}
              className="h-7 w-7 p-0 text-slate-400 hover:text-slate-700"
              data-testid="expand-editor-btn"
              title={editorExpanded ? "Collapse editor" : "Expand editor"}
            >
              {editorExpanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </Button>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Start drafting your contract content here, or select a template..."
              value={formData.content}
              onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
              className="min-h-[500px] rounded-sm font-sans text-base leading-relaxed"
              data-testid="contract-content-textarea"
            />
          </CardContent>
        </Card>
      )}
    </>
  );
}

function SectionContent({ section, formData, setFormData, onUpdateSection, setActiveSection }) {
  if (section.type === "deliverables") {
    return (
      <div className="space-y-3">
        <Textarea
          value={section.content}
          onChange={(e) => onUpdateSection(section.id, e.target.value)}
          placeholder="Describe the overall deliverables scope..."
          rows={4}
          className="rounded-sm"
          data-testid={`section-${section.id}-textarea`}
        />
        <div className="border-t pt-3">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Itemised Deliverables</Label>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={() => setFormData((prev) => ({
                ...prev,
                deliverables: [...prev.deliverables, { id: `del-${Date.now()}`, description: "", delivery_date: "" }],
              }))}
              data-testid="add-deliverable-btn"
            >
              <Plus size={12} />Add Deliverable
            </Button>
          </div>
          {formData.deliverables.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-3">No deliverables added yet.</p>
          )}
          <div className="space-y-2">
            {formData.deliverables.map((del, idx) => (
              <div key={del.id} className="grid grid-cols-[1fr_150px_28px] gap-2 items-start">
                <Input
                  value={del.description}
                  onChange={(e) => {
                    const updated = [...formData.deliverables];
                    updated[idx] = { ...updated[idx], description: e.target.value };
                    setFormData((prev) => ({ ...prev, deliverables: updated }));
                  }}
                  placeholder={`Deliverable ${idx + 1}`}
                  className="rounded-sm h-9"
                  data-testid={`deliverable-description-${idx}`}
                />
                <Input
                  type="date"
                  value={del.delivery_date}
                  onChange={(e) => {
                    const updated = [...formData.deliverables];
                    updated[idx] = { ...updated[idx], delivery_date: e.target.value };
                    setFormData((prev) => ({ ...prev, deliverables: updated }));
                  }}
                  className="rounded-sm h-9"
                  data-testid={`deliverable-date-${idx}`}
                />
                <button
                  className="h-9 w-7 flex items-center justify-center text-slate-300 hover:text-red-500"
                  onClick={() => setFormData((prev) => ({
                    ...prev,
                    deliverables: prev.deliverables.filter((_, i) => i !== idx),
                  }))}
                  data-testid={`remove-deliverable-${idx}`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" className="gap-1 text-cyan-600" onClick={() => { setActiveSection(section.id); document.querySelector('[data-testid="ai-prompt-input"]')?.focus(); }}>
            <Sparkles size={12} />AI Assist
          </Button>
        </div>
      </div>
    );
  }

  if (section.type === "kpis") {
    return (
      <div className="space-y-3">
        <Textarea
          value={section.content}
          onChange={(e) => onUpdateSection(section.id, e.target.value)}
          placeholder="Describe the overall KPI framework..."
          rows={4}
          className="rounded-sm"
          data-testid={`section-${section.id}-textarea`}
        />
        <div className="border-t pt-3">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Itemised KPIs</Label>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={() => setFormData((prev) => ({
                ...prev,
                kpis: [...prev.kpis, { id: `kpi-${Date.now()}`, description: "", measure: "" }],
              }))}
              data-testid="add-kpi-editor-btn"
            >
              <Plus size={12} />Add KPI
            </Button>
          </div>
          {formData.kpis.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-3">No KPIs added yet.</p>
          )}
          <div className="space-y-2">
            {formData.kpis.map((kpi, idx) => (
              <div key={kpi.id} className="grid grid-cols-[1fr_1fr_28px] gap-2 items-start">
                <Input
                  value={kpi.description}
                  onChange={(e) => {
                    const updated = [...formData.kpis];
                    updated[idx] = { ...updated[idx], description: e.target.value };
                    setFormData((prev) => ({ ...prev, kpis: updated }));
                  }}
                  placeholder={`KPI ${idx + 1} description`}
                  className="rounded-sm h-9"
                  data-testid={`kpi-description-${idx}`}
                />
                <Input
                  value={kpi.measure}
                  onChange={(e) => {
                    const updated = [...formData.kpis];
                    updated[idx] = { ...updated[idx], measure: e.target.value };
                    setFormData((prev) => ({ ...prev, kpis: updated }));
                  }}
                  placeholder="e.g., 95% on-time"
                  className="rounded-sm h-9"
                  data-testid={`kpi-measure-${idx}`}
                />
                <button
                  className="h-9 w-7 flex items-center justify-center text-slate-300 hover:text-red-500"
                  onClick={() => setFormData((prev) => ({
                    ...prev,
                    kpis: prev.kpis.filter((_, i) => i !== idx),
                  }))}
                  data-testid={`remove-kpi-${idx}`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" className="gap-1 text-cyan-600" onClick={() => { setActiveSection(section.id); document.querySelector('[data-testid="ai-prompt-input"]')?.focus(); }}>
            <Sparkles size={12} />AI Assist
          </Button>
        </div>
      </div>
    );
  }

  if (section.is_editable) {
    return (
      <div className="space-y-2">
        <Textarea
          value={section.content}
          onChange={(e) => onUpdateSection(section.id, e.target.value)}
          placeholder={`Enter ${section.name} content...`}
          rows={8}
          className="rounded-sm"
          data-testid={`section-${section.id}-textarea`}
        />
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-cyan-600"
            onClick={() => {
              setActiveSection(section.id);
              document.querySelector('[data-testid="ai-prompt-input"]')?.focus();
            }}
          >
            <Sparkles size={12} />
            AI Assist
          </Button>
        </div>
      </div>
    );
  }

  return (
    <LockedSectionView
      section={section}
      onOverrideEdit={(content) => onUpdateSection(section.id, content)}
    />
  );
}
